import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../../services/api';
import { Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import Pill from '../../ui/Pill';
import { ErrorNotice, InfoNotice, SkeletonRows } from '../../ui/States';
import { errorMessage, inputClass, label, money, selectClass, toCents } from '../../../lib/programs';
import { EventSteps, StepEntry, dateText } from '../../../lib/eventMoney';
import type { SimplePanelProps } from '../adapt';
import type {
  PaymentClaimLineType,
  PaymentClaimLinesRequest,
  PaymentRouteRequest,
  PaymentStepRequest,
} from '../../../contracts';
import Help from '../../ui/Help';

const stepsKey = (eventId: string, kinds: string[]) => ['event-money-steps', eventId, kinds.join(',')];

function ClaimLines({
  entry,
  types,
  onClose,
  onSaved,
}: {
  entry: StepEntry;
  types: Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [lines, setLines] = useState(
    entry.claimLines.length
      ? entry.claimLines.map((l) => ({
          type: l.type,
          amount: l.amountCents == null ? '' : (l.amountCents / 100).toFixed(2),
          note: l.note ?? '',
        }))
      : [{ type: 'airfare', amount: '', note: '' }],
  );
  const save = useMutation(
    async () =>
      api.put(`/payments/${entry.id}/claim-lines`, {
        lines: lines
          .filter((l) => l.amount !== '' || l.note)
          .map((l, i) => {
            const c = toCents(l.amount);
            if (Number.isNaN(c)) throw new Error(`Line ${i + 1}: enter an amount like 412.50`);
            return { type: l.type as PaymentClaimLineType, amountCents: c, note: l.note };
          }),
      } satisfies PaymentClaimLinesRequest),
    { onSuccess: onSaved },
  );
  const total = lines.reduce((s, l) => s + (toCents(l.amount) || 0), 0);
  return (
    <Modal
      title={`Claim lines · ${entry.person.name}`}
      onClose={onClose}
      width={640}
      footer={
        <div className="flex justify-end gap-2 w-full">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      }
    >
      {save.error ? (
        <ErrorNotice>{errorMessage(save.error, (save.error as Error).message || 'Could not save.')}</ErrorNotice>
      ) : null}
      <p className="text-small text-ink-2 mb-2">
        One line per expense on the claim. The entry’s amount becomes the total. Never type card or bank numbers.
      </p>
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-6 gap-2 mb-1 text-small">
          <select
            className={`${inputClass} col-span-2`}
            value={l.type}
            onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))}
          >
            {Object.entries(types).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <input
            className={inputClass}
            value={l.amount}
            placeholder="$"
            inputMode="decimal"
            onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
          />
          <input
            className={`${inputClass} col-span-3`}
            value={l.note}
            placeholder="note"
            onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))}
          />
        </div>
      ))}
      <div className="flex justify-between text-small mt-2">
        <button className="underline" onClick={() => setLines([...lines, { type: 'taxi', amount: '', note: '' }])}>
          Add a line
        </button>
        <span>Total {money(total)}</span>
      </div>
    </Modal>
  );
}

function EntryRow({
  e,
  data,
  eventId,
  kinds,
  canEdit,
}: {
  e: StepEntry;
  data: EventSteps;
  eventId: string;
  kinds: string[];
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries(stepsKey(eventId, kinds));
    queryClient.invalidateQueries(['event-ledger', eventId]);
    queryClient.invalidateQueries(['event-money', eventId]);
  };
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState(false);
  const setRoute = useMutation(async (body: PaymentRouteRequest) => api.put(`/payments/${e.id}/route`, body), {
    onSuccess: refresh,
  });
  const step = useMutation(
    async ({ key, undo }: { key: string; undo: boolean }) =>
      api.post(`/payments/${e.id}/steps/${key}`, { undo } satisfies PaymentStepRequest),
    { onSuccess: refresh },
  );
  const done = e.steps.filter((s) => s.done && !s.prerequisite).length;
  const total = e.steps.filter((s) => !s.prerequisite && !s.optional).length;
  const finished = ['paid', 'cleared'].includes(e.status);
  return (
    <>
      <tr className="border-b border-line align-top">
        <td className="py-[7px] pr-3">
          {e.person.name}
          <div className="text-small text-ink-2">{e.person.email ?? ''}</div>
        </td>
        <td className="py-[7px] pr-3">
          {label(e.kind)}
          <div className="text-small text-ink-2">
            {label(e.status)} · {money(e.amountCents)}
          </div>
        </td>
        <td className="py-[7px] pr-3">
          {canEdit ? (
            <select
              className={selectClass}
              value={e.route ?? ''}
              disabled={setRoute.isLoading || e.status === 'cancelled'}
              onChange={(ev) => setRoute.mutate({ route: (ev.target.value || null) as PaymentRouteRequest['route'] })}
            >
              <option value="">— choose how it is paid</option>
              {(data.routes ?? []).map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
          ) : (
            (e.routeLabel ?? '—')
          )}
        </td>
        <td className="py-[7px] pr-3">
          {e.route ? (
            <>
              <div>
                {done} of {total} steps
                {e.next && !finished ? <span className="text-ink-2"> · next: {e.next}</span> : null}
              </div>
              {e.stuck ? (
                <Pill tone="failed">{`Waiting ${e.stuck.days} days`}</Pill>
              ) : finished ? (
                <Pill tone="healthy">Done</Pill>
              ) : null}
            </>
          ) : (
            <span className="text-ink-2">—</span>
          )}
        </td>
        <td className="py-[7px] whitespace-nowrap">
          {e.route ? (
            <button className="underline" onClick={() => setOpen((v) => !v)}>
              {open ? 'Hide steps' : 'Steps'}
            </button>
          ) : null}
          {e.kind === 'reimbursement' ? (
            <button className="underline ml-3" onClick={() => setLines(true)}>
              Claim lines{e.claimLines.length ? ` (${e.claimLines.length})` : ''}
            </button>
          ) : null}
        </td>
      </tr>
      {open && e.route ? (
        <tr className="border-b border-line bg-[#fafafa]">
          <td colSpan={5} className="py-2 px-3">
            <ul className="text-small grid grid-cols-1 md:grid-cols-2 gap-x-6">
              {e.steps.map((s) => (
                <li key={s.key} className="flex items-center gap-2 py-[2px]">
                  <input
                    type="checkbox"
                    checked={s.done}
                    disabled={!canEdit || step.isLoading || e.status === 'cancelled'}
                    onChange={() => step.mutate({ key: s.key, undo: s.done })}
                    aria-label={s.label}
                  />
                  <span className={s.done ? '' : 'text-ink-2'}>
                    {s.prerequisite ? 'Before: ' : ''}
                    {s.label}
                    {s.optional ? ' (if needed)' : ''}
                  </span>
                  {s.done ? <span className="text-small text-ink-2">{dateText(s.at)}</span> : null}
                </li>
              ))}
            </ul>
            {e.route === 'member_travel' && canEdit ? (
              <label className="flex items-center gap-2 text-small mt-2">
                <input
                  type="checkbox"
                  checked={e.lostReceipt}
                  onChange={() => setRoute.mutate({ lostReceipt: !e.lostReceipt })}
                />{' '}
                A receipt is lost (adds the attestation signed by the approver)
              </label>
            ) : null}
            {step.error || setRoute.error ? (
              <ErrorNotice>{errorMessage(step.error || setRoute.error, 'Could not save.')}</ErrorNotice>
            ) : null}
          </td>
        </tr>
      ) : null}
      {lines ? (
        <ClaimLines
          entry={e}
          types={data.claimLineTypes ?? {}}
          onClose={() => setLines(false)}
          onSaved={() => {
            setLines(false);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

function YorkSteps({ eventId, canEdit, kinds, title }: SimplePanelProps & { kinds: string[]; title: string }) {
  const queryClient = useQueryClient();
  const q = useQuery<EventSteps>(
    stepsKey(eventId, kinds),
    async () => (await api.get(`/events/${eventId}/money/steps`, { params: { kinds: kinds.join(',') } })).data.data,
  );
  const defaults = useMutation(async () => api.post(`/events/${eventId}/money/default-routes`, { kinds }), {
    onSuccess: () => queryClient.invalidateQueries(stepsKey(eventId, kinds)),
  });
  if (q.isLoading) return <SkeletonRows rows={4} />;
  if (q.error || !q.data) return <ErrorNotice>{errorMessage(q.error, 'Could not load the steps.')}</ErrorNotice>;
  const d = q.data;
  const c = d.counts;
  return (
    <Panel>
      <PanelHeader title={title} note={`${c.total} · ${c.paid} done · ${c.stuck} waiting more than 14 days`}>
        {canEdit && c.total > c.withRoute ? (
          <Button size="sm" disabled={defaults.isLoading} onClick={() => defaults.mutate()}>
            Use the usual route for the {c.total - c.withRoute} without one
          </Button>
        ) : null}
      </PanelHeader>
      <PanelBody className="text-small">
        <Help>
          How each person is paid decides the steps: a travel claim goes through York’s six steps (documents →
          PeopleSoft → claim sent for signing → signed → Concur → reimbursed); an honorarium, a non-resident payment, a
          participant fee, an invoice to another institution and a pre-paid flight each have their own. Ticking a step
          moves the payment’s status forward. Only that a form was done is recorded — never banking details.
        </Help>
        {!d.namesVisible ? (
          <InfoNotice>Counts only: people and amounts are shown to admins.</InfoNotice>
        ) : d.entries.length ? (
          <table className="w-full">
            <thead>
              <tr className="text-left text-small text-ink-2 border-b border-line-strong">
                <th className="py-2 pr-3">Person</th>
                <th className="py-2 pr-3">Payment</th>
                <th className="py-2 pr-3">How it is paid</th>
                <th className="py-2 pr-3">Where it stands</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {d.entries.map((e) => (
                <EntryRow key={e.id} e={e} data={d} eventId={eventId} kinds={kinds} canEdit={canEdit} />
              ))}
            </tbody>
          </table>
        ) : (
          <p>None yet.</p>
        )}
        {defaults.error ? <ErrorNotice>{errorMessage(defaults.error, 'Could not set the routes.')}</ErrorNotice> : null}
      </PanelBody>
    </Panel>
  );
}

export function ReimbursementSteps(props: SimplePanelProps) {
  return <YorkSteps {...props} kinds={['reimbursement']} title="York steps for each reimbursement" />;
}

export function HonorariaSteps(props: SimplePanelProps) {
  return <YorkSteps {...props} kinds={['honorarium', 'gift_card']} title="York steps for honoraria and gifts" />;
}
